import { useEffect, useRef, useState } from 'react';
import {
	Button,
	ReferenceInput,
	AutocompleteInput,
	SelectInput,
	SimpleForm,
	TextInput,
	useDataProvider,
	useGetMany,
	useGetOne,
	useNotify
} from 'react-admin';
import { useFormContext, useWatch } from 'react-hook-form';
import { Box, Tab, Tabs, Typography } from '@mui/material';
import { default as icon } from '@mui/icons-material/Preview';
import PromptTextBlock from '../components/PromptTextBlock';

const stageChoices = [
	{ id: 'start', name: 'Start' },
	{ id: 'interview', name: 'Interview' },
	{ id: 'summary', name: 'Summary' }
];

const ModuleInput = () => {
	const { setValue } = useFormContext();
	const sessionId = useWatch({ name: 'session_id' });
	const previousSessionId = useRef( sessionId );
	const { data: session } = useGetOne(
		'sessions',
		{ id: sessionId },
		{ enabled: !!sessionId }
	);
	const sessionModules = session?.session_modules || [];
	const { data: modules = [] } = useGetMany(
		'modules',
		{ ids: sessionModules.map( row => row.module_id ) },
		{ enabled: sessionModules.length > 0 }
	);

	const choices = sessionModules.map( row => ({
		id: row.module_id,
		name: modules.find( module => String( module.id ) === String( row.module_id ) )?.title || `Module ${row.module_id}`
	}));

	useEffect(() => {
		if ( previousSessionId.current !== sessionId ) {
			previousSessionId.current = sessionId;
			setValue( 'module_id', undefined );
		}
	}, [ sessionId, setValue ]);

	if ( !sessionId ) {
		return null;
	}

	return (
		<SelectInput
			source="module_id"
			label="Module"
			choices={choices}
			disabled={choices.length === 0}
			helperText={choices.length === 0 ? 'Selected session has no modules attached' : false}
		/>
	);
};

const PreviewSubmitButton = () => {
	const sessionId = useWatch({ name: 'session_id' });
	const stage = useWatch({ name: 'stage' });

	return (
		<Button
			type="submit"
			label="Preview"
			disabled={!sessionId || !stage}
		/>
	);
};

const PreviewButton = ({ setPreview }) => {
	const dataProvider = useDataProvider();
	const notify = useNotify();

	const handleSubmit = async ( values ) => {
		if ( !values.session_id ) {
			notify( 'Select a Session before previewing.', { type: 'warning' } );
			return;
		}

		if ( !values.stage ) {
			notify( 'Select a Stage before previewing.', { type: 'warning' } );
			return;
		}

		try {
			const { data } = await dataProvider.previewPrompt( values );
			setPreview( data );
		} catch ( error ) {
			notify( error.body?.error || error.message || 'Prompt preview failed', { type: 'error' } );
		}
	};

	return (
		<SimpleForm onSubmit={handleSubmit} toolbar={false}>
			<ReferenceInput source="session_id" reference="sessions">
				<AutocompleteInput label="Session" />
			</ReferenceInput>
			<ModuleInput />
			<SelectInput source="stage" choices={stageChoices} defaultValue="interview" />
			<TextInput source="interview_id" label="Interview ID (optional)" />
			<PreviewSubmitButton />
		</SimpleForm>
	);
};

const HighlightedPrompt = ({ preview }) => {
	return (
		<PromptTextBlock
			text={preview.pre_rendered_prompt || preview.prompt || ''}
			values={preview.values || {}}
		/>
	);
};

const PreviewTabs = ({ preview }) => {
	const [ tab, setTab ] = useState( 'rendered' );

	return (
		<Box sx={{ mt: 2 }}>
			<Tabs value={tab} onChange={(event, value) => setTab(value)}>
				<Tab label="Rendered" value="rendered" />
				<Tab label="Pre-rendered" value="pre-rendered" />
			</Tabs>
			{tab === 'rendered' && (
				<>
					<Typography variant="h6" sx={{ mt: 2 }}>Rendered Prompt</Typography>
					<HighlightedPrompt preview={preview} />
				</>
			)}
			{tab === 'pre-rendered' && (
				<>
					<Typography variant="h6" sx={{ mt: 2 }}>Pre-rendered Prompt</Typography>
					<PromptTextBlock text={preview.pre_rendered_prompt} />
				</>
			)}
		</Box>
	);
};

const PromptPreviewList = () => {
	const [ preview, setPreview ] = useState( null );

	return (
		<Box sx={{ p: 2 }}>
			<Typography variant="h5">Prompt Preview</Typography>
			<PreviewButton setPreview={setPreview} />
			{preview && <PreviewTabs preview={preview} />}
		</Box>
	);
};

export const PromptPreview = { list: PromptPreviewList, icon };

export default PromptPreview;
